import React, { forwardRef, useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  IconButton,
  Grow,
  Typography,
  Button,
  Box,
  Grid,
  TextField,
} from '@mui/material'
import { motion } from 'framer-motion'
import { Close, Delete, Save } from '@mui/icons-material'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { v4 as uuidv4 } from 'uuid'
import { useModal } from 'components/modals'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

const Transition = forwardRef(function Transition(props, ref) {
  return <Grow timeout={300} ref={ref} {...props} />
})

export default function AddChildModal({
  dependents = [],
  child: initialChild,
  updateIntakeStateAndSubmit,
  updateIntakeStateAtKey,
  onClose,
}) {
  const emptyChild = { id: null, name: '', age: '' }
  const [child, setChild] = useState(initialChild || emptyChild)
  const { openModal } = useModal()

  const handleChange = (field, value) => {
    setChild({ ...child, [field]: value })
  }

  const resetForm = () => setChild(emptyChild)

  const saveDependents = (updatedDependents) => {
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({
        familyDependents: updatedDependents,
        familyChildren: updatedDependents.length,
      })
    )()
  }

  const handleSave = (addAnother = false) => {
    let updatedDependents
    if (child.id && dependents.some((d) => d.id === child.id)) {
      updatedDependents = dependents.map((d) =>
        d.id === child.id ? { ...child } : d
      )
    } else {
      updatedDependents = [...dependents, { ...child, id: uuidv4() }]
    }

    saveDependents(updatedDependents)

    if (addAnother) {
      onClose()
      resetForm()
      openModal('addChild', {
        dependents: updatedDependents,
        updateIntakeStateAndSubmit,
        updateIntakeStateAtKey,
      })
    } else {
      onClose()
    }
  }

  const handleDelete = () => {
    const updatedDependents = dependents.filter((d) => d.id !== child.id)
    saveDependents(updatedDependents)
    onClose()
  }

  const handleCancel = () => onClose()

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600)
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <Dialog
      open
      onClose={handleCancel}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { overflow: 'visible' } }}
    >
      <DialogContent sx={{ overflow: 'visible' }}>
        <motion.div
          key="editor"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          {/* Close button */}
          <Box
            sx={{
              justifyContent: 'space-between',
              position: 'relative',
              top: -64,
            }}
          >
            <IconButton
              onClick={handleCancel}
              sx={{
                position: 'absolute',
                top: 0,
                right: -16,
                color: '#fff',
                zIndex: 1,
                backgroundColor: '#000',
                '&:hover': { backgroundColor: '#000' },
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Content */}
          <Box p={isMobile ? 1 : 3}>
            <Box mb={3}>
              <Typography variant="headerTitle" sx={{ color: '#262627' }}>
                {initialChild ? 'Edit Child' : 'Add Child'}
              </Typography>
            </Box>

            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  inputType: 'text',
                  placeholder: 'Name',
                  label: `Name`,
                  value: child.name,
                  onChange: (e) => handleChange('name', e),
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  inputType: 'int',
                  placeholder: 'Age',
                  label: `Age`,
                  value: child.age,
                  onChange: (e) => handleChange('age', e),
                })}
              </Grid>
            </Grid>

            {/* Footer buttons */}
            <Box
              display="flex"
              justifyContent={isMobile ? 'flex-end' : 'space-between'}
              mt={3}
            >
              <Button
                onClick={handleCancel}
                variant="parcoRounded"
                color="outline"
                sx={{ display: isMobile ? 'none' : 'inline-flex' }}
              >
                Cancel
              </Button>

              <Box>
                {initialChild && (
                  <Button
                    variant="parcoRounded"
                    color="outlineError"
                    onClick={handleDelete}
                    sx={{ '& svg': { height: '27px', width: '24px' } }}
                  >
                    Delete
                  </Button>
                )}
                {!initialChild && (
                  <Button
                    variant="parcoRounded"
                    color={isMobile ? 'outline' : 'primary'}
                    onClick={() => handleSave(true)}
                    // sx={{ display: isMobile ? 'none' : 'inline-flex' }}
                  >
                    {isMobile ? 'Add Another' : 'Save & Add Another'}
                  </Button>
                )}
                <Button
                  variant="parcoRounded"
                  onClick={() => handleSave(false)}
                  sx={{
                    ml: { xs: 1, sm: 2 },
                    '& svg': { height: '27px', width: '24px' },
                  }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </DialogContent>
    </Dialog>
  )
}
